// Reloading the page when IndexedDB cannot be reopened (GitHub issue #3906).
//
// iOS kills WebKit's Networking process, which hosts the IndexedDB server, while the app sits in
// the background. On WebKit before iOS 26.5 the page then keeps a proxy to the dead connection, so
// every later indexedDB.open() from it fails with "Connection to Indexed Database server lost" until
// a navigation creates a new document (WebKit bug 309386). Retrying does not help - the flag behind
// that error is never reset - so the only recovery is the one the message asks for: a reload.
//
// The reload goes through the update gate like a new bundle does (app_update.ts): at once unless a
// song is playing or was only just paused, or the user is in the middle of something. A marker in
// localStorage, which survives the reload (the same crash that kills IndexedDB leaves localStorage
// working), guards against a loop: at most one reload per reloadCoolDownMs, and the report of a
// failure inside that window says whether the reload had helped (an operation succeeded on the
// new page) or not. After the window a fresh failure is reloaded for again, so a stale marker
// never pins a page to its dead storage. A reload still waiting for the gate is withdrawn from it when
// an operation succeeds, since storage that works again needs no reload (and its marker would hold back
// a genuine one); once the gate has let it go, it is past cancelling. A browser keeps
// running the document after the reload call until the navigation commits, so for reloadSurvivalMs a
// failure still finds the reload already scheduled; a document still running after that survived it (a
// webview that did not navigate) and decides from the marker again, so its later failures are reported
// as unrecovered. The marker names the document that wrote it, since a success on that document is no
// evidence that a reload helped.
//
// idb.ts decides and schedules in two steps - decideDeadStorageReload(), then
// scheduleDeadStorageReload() - so that the report it files about the failure can say what was
// decided before the reload, which can fire at once, takes the page away.

import { localStorageGet, LocalStorageKey, localStorageSet } from '../data/client_local_storage.ts';
import { cancelUpdate, runWhenUpdateAllowed } from '../player/update_gate.ts';
import { describeAppReloadTiming } from './app_update.ts';

export type DeadStorageReloadDecision =
  | { kind: 'scheduled'; timing: string } // timing: when the gate will let it reload, as a log phrase
  | { kind: 'already scheduled' }
  | { kind: 'skipped'; reason: string };

export const reloadCoolDownMs = 10 * 60_000;
// how long the reload waits for the report about the failure to be sent before going ahead anyway
const reportWaitMs = 2000;
// how long after the reload call the document must still be running to count as having survived it, and
// until then an operation failing is part of the reload going ahead. Generous, since a navigation can be
// slow right after iOS killed the Networking process, and a failure judged too early files a false "did
// not help" report; the only cost is that a webview that never navigates is reported 30 s later.
const reloadSurvivalMs = 30_000;
// distinct from the 'app-reload' and 'song-files' keys, so neither evicts this one
const storageReloadUpdateKey = 'storage-reload';

// 'waiting' for the gate (cancellable), then 'running' from the moment the gate lets it go until
// reloadSurvivalMs after reloadPage() was called
let reloadState: 'none' | 'waiting' | 'running' = 'none';
// bumped by every schedule and by the test reset, so a reloadNow still awaiting its report from an earlier
// schedule (in practice, an earlier test) does not reload or start a survival timer for a later one
let reloadAttempt = 0;
let survivalTimer: ReturnType<typeof setTimeout> | undefined;
let recoveryChecked = false;
// Which document wrote the marker, and when this one started: a marker this document wrote, or one another
// document (a second tab) wrote after this one started, was not written before the reload that produced
// this page.
let documentId = newDocumentId();
let documentStartedAt = Date.now();
let reloadPage: () => void = () => window.location.reload();

// whether the page is going to reload, or has just been told to: any failure until then is handled by it
export function isDeadStorageReloadPending(): boolean {
  return reloadState !== 'none';
}

export function decideDeadStorageReload(now = Date.now()): DeadStorageReloadDecision {
  if (reloadState !== 'none') {
    return { kind: 'already scheduled' };
  }
  const previous = localStorageGet(LocalStorageKey.DeadStorageReload);
  if (previous && now - previous.reloadedAt < reloadCoolDownMs) {
    const secondsAgo = Math.round((now - previous.reloadedAt) / 1000);
    return {
      kind: 'skipped',
      reason:
        previous.recoveredAt === undefined
          ? `the page already reloaded for this ${secondsAgo} s ago and it did not help`
          : `the page reloaded for this ${secondsAgo} s ago, so it is not reloaded again yet`,
    };
  }
  return { kind: 'scheduled', timing: describeAppReloadTiming(now) };
}

// Reloads once the gate allows. `fileReport` is called first and given reportWaitMs to finish, so the
// reload does not cancel the request that reports the failure.
export function scheduleDeadStorageReload(fileReport: () => Promise<void>): void {
  reloadState = 'waiting';
  reloadAttempt++;
  runWhenUpdateAllowed(
    () => {
      reloadState = 'running';
      void reloadNow(fileReport);
    },
    // exclusive: the page is going away; waitForUserIdle: a reload loses an uncommitted planner cell
    { key: storageReloadUpdateKey, exclusive: true, waitForUserIdle: true },
  );
}

async function reloadNow(fileReport: () => Promise<void>): Promise<void> {
  const attempt = reloadAttempt;
  // A throw from the marker write (a full or private-mode localStorage) or from the reload call leaves the
  // page where it was, so the state goes back at once and the next failure is decided afresh. The report
  // never rejects (reportCrashDescription catches).
  try {
    localStorageSet(LocalStorageKey.DeadStorageReload, { reloadedAt: Date.now(), documentId });
    await Promise.race([fileReport(), new Promise((resolve) => setTimeout(resolve, reportWaitMs))]);
    if (attempt !== reloadAttempt) {
      return; // superseded while it waited: a later schedule owns the reload
    }
    console.log('Reloading the page for its dead IndexedDB connection (GitHub issue #3906)');
    reloadPage();
  } catch (e) {
    console.error(
      'Writing the dead-storage marker or reloading the page for its dead IndexedDB connection failed (GitHub issue #3906)',
      e,
    );
    reloadState = 'none';
    return;
  }
  // still here then: the document survived the reload, so the next failure decides from the marker
  survivalTimer = setTimeout(() => {
    survivalTimer = undefined;
    reloadState = 'none';
  }, reloadSurvivalMs);
}

// Called when an operation succeeds. A reload still waiting for the gate is no longer needed: it is
// withdrawn from the gate, and true says so. Once the gate has let it go, it is past cancelling.
export function cancelDeadStorageReload(): boolean {
  if (reloadState !== 'waiting') {
    return false;
  }
  reloadState = 'none';
  cancelUpdate(storageReloadUpdateKey);
  return true;
}

// Called when an operation succeeds. The first success of a page load that followed a dead-storage
// reload is the evidence that the reload helped: it is recorded in the marker (which also ends the
// loop guard's "did not help" verdict) and described for a report. Every later call returns nothing.
export function noteStorageWorking(now = Date.now()): string | undefined {
  if (recoveryChecked) {
    return undefined;
  }
  recoveryChecked = true;
  const previous = localStorageGet(LocalStorageKey.DeadStorageReload);
  if (!previous || previous.recoveredAt !== undefined) {
    return undefined;
  }
  if (previous.documentId === documentId || previous.reloadedAt > documentStartedAt) {
    // this document wrote it (its reload threw or did not navigate), or another document wrote it after this
    // one started: either way this page is not the one its reload produced
    return undefined;
  }
  localStorageSet(LocalStorageKey.DeadStorageReload, { ...previous, recoveredAt: now });
  return `${Math.round((now - previous.reloadedAt) / 1000)} s after the page reloaded for its dead connection`;
}

function newDocumentId(): string {
  return Math.random().toString(36).slice(2);
}

// test-only: the flags are module state, and jsdom cannot reload a page; a reset stands for a new page load
export function resetStorageReloadForTests(reloadPageOverride?: () => void): void {
  reloadState = 'none';
  reloadAttempt++;
  documentId = newDocumentId();
  documentStartedAt = Date.now();
  clearTimeout(survivalTimer);
  survivalTimer = undefined;
  recoveryChecked = false;
  reloadPage = reloadPageOverride ?? (() => window.location.reload());
}
